import { FREQUENCY_BUCKETS } from "@kensio/pinyinjs";

/**
 * How common a word is in general, as opposed to in the text on the page.
 *
 * The segmenter orders by two different things and a reader has to be able to
 * tell them apart. One is how often this text uses a word, which the text
 * decides. The other is how often anybody uses it, which is what this answers.
 * A text about banking uses 银行 twenty times and 的 five, and only one of
 * those two is a common word.
 *
 * The number comes from the corpus behind `@kensio/pinyinjs`, quantised to
 * sixteen levels and carried inside every dictionary tier. Nothing extra is
 * fetched to read it. `Dictionary.frequencyOf` reads a word's bucket, off the
 * same key search `lookup` runs and without decoding an entry, so what is left
 * here is what the site calls the number.
 *
 * There was a binary search over `wordAt` in this file until
 * KensioSoftware/pinyinjs#169 landed that method in 1.7.11. It went with the
 * search, and a 繁體 word now normalises the way `lookup` normalises it: 齶
 * reports the 5 its normalised key was counted at rather than the 0 that says
 * the corpus never met this spelling.
 */

/** What a band is called, and where it starts on the sixteen-level scale. */
export interface FrequencyBand {
  /** What the column says. */
  readonly name: string;

  /** The lowest bucket this band covers. */
  readonly from: number;
}

/**
 * The five bands, commonest first.
 *
 * **Sixteen levels is more resolution than the page can use and less than it
 * looks.** Measured over the 8,828 HSK words the standard tier holds, the
 * buckets in use run 4 to 15 and two thirds of that vocabulary sits in three of
 * them. Printing a number would offer a precision the scale does not have, and
 * over the 264 distinct words in 1,000 characters of running text the sixteen
 * levels resolve to eleven with 71 words sharing one.
 *
 * The cuts are a judgement over that distribution rather than an even split.
 * 是 lands at 15 and 的 and 我 at 14, which is the grammatical core and is its
 * own band. 学习, 喜欢 and 银行 all sit at 10. 嘲笑 is 7, 推诿 5 and 拗口 4.
 *
 * Sorting uses the bucket and never these bands, so a reader ordering the list
 * by this column gets the full sixteen levels and sees five names.
 */
export const FREQUENCY_BANDS: readonly FrequencyBand[] = [
  { name: "very common", from: 13 },
  { name: "common", from: 10 },
  { name: "fairly common", from: 7 },
  { name: "uncommon", from: 4 },
  { name: "rare", from: 0 },
];

/**
 * What a bucket is called.
 *
 * A word the dictionary has no entry for has no bucket and no band. That is a
 * character or a run the tier does not hold, and saying "rare" about it would
 * be claiming a measurement nobody made.
 */
export function frequencyBandOf(
  bucket: number | undefined,
): FrequencyBand | undefined {
  if (bucket === undefined || bucket < 0 || bucket >= FREQUENCY_BUCKETS) {
    return undefined;
  }

  return FREQUENCY_BANDS.find((band) => bucket >= band.from);
}
